import { useEffect, useMemo, useState } from 'react'
import { Button, Select, TextInput } from '@mantine/core'
import { IconSelector } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { HelpTip } from '../v2/HelpTip'
import { V2Modal, V2ModalActions } from '../v2/V2Modal'
import { formatLabel } from '../preview/FormatSwitcher'
import { confirmTemplateChange, templateLabel } from './confirmTemplateChange'
import classes from './CampaignSettingsModal.module.css'

const FULL_FORMAT_NAMES = {
  'medium-rectangle': 'Medium Rectangle',
  'half-page': 'Halfpage',
  billboard: 'Billboard',
}

function formatCaption(format) {
  const name = FULL_FORMAT_NAMES[format.id] || formatLabel(format)
  return `${name} ${format.width} x ${format.height}`
}

/** Grey mini-ad of a format, drawn like the Figma "placeholder ads". */
function FormatIllustration({ format }) {
  const width = Number(format.width) || 300
  const height = Number(format.height) || 250
  if (width >= height * 2.5) {
    return (
      <svg width="176" height="56" viewBox="0 0 176 56" aria-hidden="true">
        <rect width="176" height="56" rx="2" fill="#f2f2f2" />
        <rect width="88" height="56" fill="#a1a1a1" />
        <circle cx="10" cy="8" r="4" fill="#f2f2f2" />
        <rect x="17" y="5" width="26" height="6" rx="3" fill="#f2f2f2" />
        <rect x="104" y="11" width="56" height="7" rx="3.5" fill="#a1a1a1" />
        <rect x="100" y="21" width="64" height="7" rx="3.5" fill="#a1a1a1" />
        <rect x="115" y="32" width="34" height="9" fill="#a1a1a1" />
        <rect x="126" y="46" width="10" height="6" rx="3" fill="#a1a1a1" />
        <rect x="160" y="46" width="10" height="6" rx="3" fill="#a1a1a1" />
      </svg>
    )
  }
  if (height > width * 1.5) {
    return (
      <svg width="60" height="112" viewBox="0 0 60 112" aria-hidden="true">
        <rect width="60" height="112" rx="2" fill="#f2f2f2" />
        <rect width="60" height="60" fill="#a1a1a1" />
        <circle cx="7" cy="6" r="3.5" fill="#f2f2f2" />
        <rect x="13" y="3.5" width="22" height="5" rx="2.5" fill="#f2f2f2" />
        <rect x="14" y="70" width="32" height="4" rx="2" fill="#a1a1a1" />
        <rect x="10" y="78" width="40" height="4" rx="2" fill="#a1a1a1" />
        <rect x="18" y="87" width="24" height="6" fill="#a1a1a1" />
        <rect x="25" y="99" width="10" height="5" rx="2.5" fill="#a1a1a1" />
      </svg>
    )
  }
  return (
    <svg width="126" height="105" viewBox="0 0 126 105" aria-hidden="true">
      <rect width="126" height="105" rx="4" fill="#a1a1a1" />
      <rect x="63" width="63" height="105" fill="#f2f2f2" />
      <rect x="4" y="92" width="10" height="8" rx="4" fill="#f2f2f2" />
      <rect x="108" y="92" width="10" height="8" rx="4" fill="#a1a1a1" />
      <circle cx="76" cy="10" r="6" fill="#a1a1a1" />
      <rect x="84" y="6" width="32" height="8" rx="4" fill="#a1a1a1" />
      <rect x="68" y="30" width="45" height="8" rx="4" fill="#a1a1a1" />
      <rect x="68" y="41" width="52" height="8" rx="4" fill="#a1a1a1" />
      <rect x="68" y="52" width="52" height="8" rx="4" fill="#a1a1a1" />
      <rect x="68" y="67" width="22" height="8" rx="4" fill="#a1a1a1" />
    </svg>
  )
}

function languageOptions(schema) {
  for (const section of schema?.sections || []) {
    const field = (section.fields || []).find((entry) => entry.path === 'ad.language')
    if (field) return (field.options || []).map((option) => ({ value: option.value, label: option.label || option.value }))
  }
  return []
}

function templateOptions(templates, current, t) {
  const options = (templates || []).map((template) => ({
    value: template.releaseKey || template.id,
    label: templateLabel(template),
  }))
  const reference = current?.releaseKey || current?.id
  if (reference && !options.some((option) => option.value === reference)) {
    options.unshift({ value: reference, label: t('editorV2.shell.campaign.archivedTemplate', { name: current.name, version: current.version }) })
  }
  return options
}

/**
 * Settings (Figma 477:37140): template, Campaign Name, Advertiser Name, Language and the booked
 * Ad Formats; the Clickout is in the Brand card (AD-252). Save applies the choices to the
 * editor; Continue persists them with the ad.
 */
// `canChangeTemplate`: only the design team chooses an ad's template (Simon, 3B; AD-253);
// `onThirdParty`: their "Use third-party creative".
export function CampaignSettingsModal({ opened, onClose, editor, disabled = false, canChangeTemplate = true, onThirdParty = null }) {
  const { t } = useTranslation()
  const configuration = editor.configuration
  const manifest = editor.template?.manifest
  const schema = editor.template?.schema
  const campaign = editor.campaign
  const formats = manifest?.formats || []
  const languages = useMemo(() => languageOptions(schema), [schema])
  const templates = useMemo(() => templateOptions(editor.templates, manifest, t), [editor.templates, manifest, t])
  const currentTemplate = manifest?.releaseKey || manifest?.id

  const [draft, setDraft] = useState(null)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!opened || !configuration) return
    setErrors({})
    setDraft({
      template: currentTemplate,
      campaignName: configuration.ad?.campaignName || '',
      advertiserName: campaign?.advertiser?.name || configuration.data?.brand?.name || '',
      language: configuration.ad?.language || languages[0]?.value || null,
      formats: campaign?.formats?.length ? campaign.formats : formats.map((format) => format.id),
    })
  }, [opened]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }))
  const toggleFormat = (id) => {
    const next = draft.formats.includes(id)
      ? draft.formats.filter((entry) => entry !== id)
      : formats.map((format) => format.id).filter((entry) => entry === id || draft.formats.includes(entry))
    set({ formats: next })
  }

  const save = async () => {
    const next = {}
    if (!draft.campaignName.trim()) next.campaignName = t('editorV2.shell.campaign.campaignNameRequired')
    if (campaign && !draft.advertiserName.trim()) next.advertiserName = t('editorV2.shell.campaign.advertiserNameRequired')
    if (!draft.language) next.language = t('editorV2.shell.campaign.languageRequired')
    if (campaign && !draft.formats.length) next.formats = t('editorV2.shell.campaign.formatsRequired')
    setErrors(next)
    if (Object.keys(next).length) return

    const ad = {
      campaignName: draft.campaignName.trim(),
      language: draft.language,
    }
    const advertiser = campaign
      ? { ...(campaign.advertiser || { placeId: null, address: '', website: '', manual: true }), name: draft.advertiserName.trim() }
      : null
    const campaignPatch = campaign ? { advertiser, formats: draft.formats } : null

    if (draft.template && draft.template !== currentTemplate) {
      const label = templates.find((option) => option.value === draft.template)?.label || draft.template
      confirmTemplateChange({
        editor,
        reference: draft.template,
        label,
        onConfirm: async () => {
          await editor.changeTemplate(draft.template, { ad, campaign: campaignPatch })
          onClose()
        },
      })
      return
    }

    Object.entries(ad).forEach(([key, value]) => {
      if (value !== undefined && value !== configuration.ad?.[key]) editor.updateValue(`ad.${key}`, value)
    })
    if (campaignPatch) {
      editor.updateCampaign(campaignPatch)
      if (!draft.formats.includes(configuration.format?.id)) editor.updateValue('format.id', draft.formats[0])
    }
    onClose()
  }

  return (
    <V2Modal
      opened={opened}
      onClose={onClose}
      title={t('editorV2.shell.settings')}
      footer={<V2ModalActions onCancel={onClose} onConfirm={save} confirmLabel={t('editorV2.shell.campaign.save')} busy={disabled} />}
    >
      {templates.length > 1 && canChangeTemplate && (
        <Select
          label={t('editorV2.shell.campaign.template')}
          data={templates}
          value={draft.template}
          allowDeselect={false}
          rightSection={<IconSelector size={16} stroke={1.6} />}
          rightSectionPointerEvents="none"
          onChange={(value) => value && set({ template: value })}
          disabled={disabled || editor.status === 'generating'}
          comboboxProps={{ withinPortal: true }}
        />
      )}
      {onThirdParty && (
        <div>
          <Button variant="default" radius="md" disabled={disabled} onClick={() => { onClose(); onThirdParty() }}>
            {t('editorV2.shell.thirdParty.confirm')}
          </Button>
          <HelpTip label={t('editorV2.shell.campaign.thirdPartyHelp')} />
        </div>
      )}
      <TextInput
        label={t('editorV2.shell.campaign.campaignName')}
        withAsterisk
        placeholder={t('editorV2.shell.campaign.campaignNamePlaceholder')}
        value={draft.campaignName}
        onChange={(event) => set({ campaignName: event.currentTarget.value })}
        error={errors.campaignName}
        maxLength={255}
      />
      {campaign && (
        <TextInput
          label={t('editorV2.shell.campaign.advertiserName')}
          withAsterisk
          placeholder={t('editorV2.shell.campaign.advertiserNamePlaceholder')}
          value={draft.advertiserName}
          onChange={(event) => set({ advertiserName: event.currentTarget.value })}
          error={errors.advertiserName}
          maxLength={255}
        />
      )}
      <Select
        label={t('editorV2.shell.campaign.language')}
        withAsterisk
        data={languages}
        value={draft.language}
        allowDeselect={false}
        rightSection={<IconSelector size={16} stroke={1.6} />}
        rightSectionPointerEvents="none"
        onChange={(value) => value && set({ language: value })}
        error={errors.language}
        comboboxProps={{ withinPortal: true }}
      />
      {campaign && formats.length > 0 && (
        <div className={classes.block}>
          <span className={classes.label}>
            {t('editorV2.shell.campaign.adFormat')}
            <HelpTip label={t('editorV2.shell.campaign.adFormatHelp')} />
          </span>
          <div className={classes.formats} role="group" aria-label={t('editorV2.shell.campaign.adFormat')}>
            {formats.map((format) => {
              const selected = draft.formats.includes(format.id)
              return (
                <button
                  key={format.id}
                  type="button"
                  className={classes.format}
                  aria-pressed={selected}
                  data-selected={selected ? 'true' : 'false'}
                  onClick={() => toggleFormat(format.id)}
                >
                  <span className={classes.formatFrame}><FormatIllustration format={format} /></span>
                  <span className={classes.formatCaption}>{formatCaption(format)}</span>
                </button>
              )
            })}
          </div>
          {errors.formats && <span className={classes.error}>{errors.formats}</span>}
        </div>
      )}
    </V2Modal>
  )
}
